import * as React from "react";
import { cls } from "@utdanningsdirektoratet/utils/cls";
import { useId } from "@utdanningsdirektoratet/utils/useId";
import { Tooltip } from "@utdanningsdirektoratet/tooltip";
import styles from "./FileInput.module.less";

type FileInputProps = {
  onChange?: (files: Array<File>) => void;
  multiple?: boolean;
  label: React.ReactNode | string;
  className?: string;
  disabled?: boolean;
  accept?: string;
  error?: boolean;
  message?: string | React.ReactNode;
  minFileSize?: number;
  minFileSizeText?: string;
  maxFileSize?: number;
  maxFileSizeText?: string;
  maxTotalFileSizeText?: string;
};

const bytesToSize = (bytes: number) => {
  const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
  if (bytes === 0) return "0 Bytes";
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  if (i === 0) return `${bytes} ${sizes[i]}`;
  return `${(bytes / 1024 ** i).toFixed(1)} ${sizes[i]}`;
};

const FileInput = React.forwardRef<HTMLInputElement, FileInputProps>(
  (
    {
      error,
      message,
      onChange,
      accept,
      multiple,
      label,
      className,
      disabled,
      minFileSize = 0,
      minFileSizeText = `Filstørrelse kan ikke være mindre enn ${bytesToSize(
        minFileSize,
      )}`,
      maxFileSize = 30000000,
      maxFileSizeText = `Filstørrelse kan ikke overstige ${bytesToSize(
        maxFileSize,
      )}`,
      maxTotalFileSizeText = `Total filstørrelse kan ikke overstige ${bytesToSize(
        maxFileSize,
      )}`,
    },
    ref,
  ) => {
    const id = useId();
    const [fileUploadError, setFileUploadError] = React.useState<string | null>(
      null,
    );

    React.useEffect(() => {
      if (error) setFileUploadError(null);
    }, [error]);

    const uploadFile = (event: React.ChangeEvent<HTMLInputElement>): void => {
      setFileUploadError(null);
      const { files } = event.target;
      if (!files || files.length === 0) {
        return;
      }

      const filesArray = [].slice.call(files) as Array<File>;
      if (filesArray.some((file) => file.size <= minFileSize)) {
        setFileUploadError(minFileSizeText);
        return;
      }
      if (filesArray.some((file) => file.size >= maxFileSize)) {
        setFileUploadError(maxFileSizeText);
        return;
      }
      if (
        filesArray.reduce((size, file) => size + file.size, 0) >= maxFileSize
      ) {
        setFileUploadError(maxTotalFileSizeText);
        return;
      }

      if (onChange) onChange(filesArray);
    };
    const classes = cls(
      {
        [styles.fileInputLabel]: true,
        [styles.fileInputLabelDisabled]: disabled,
        [styles.fileInputLabelError]: error || !!fileUploadError,
      },
      className,
    );

    return (
      <Tooltip
        message={message ?? fileUploadError}
        disabled={!error && !fileUploadError}
        alwaysShow={error || !!fileUploadError}
        align="left"
        offset={{ position: 8 }}
        alert
      >
        {({ ref: tooltipRef, ...rest }) => (
          <label
            ref={tooltipRef}
            aria-disabled={disabled}
            id={`${id}-label`}
            className={classes}
            htmlFor={id}
          >
            {label}
            <input
              {...rest}
              ref={ref}
              aria-invalid={error}
              accept={accept}
              disabled={disabled}
              value=""
              onChange={uploadFile}
              type="file"
              multiple={multiple}
              className={styles.fileInput}
              aria-labelledby={`${id}-label`}
              id={id}
            />
          </label>
        )}
      </Tooltip>
    );
  },
);

export default FileInput;
